<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { CircleStackIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import Pts2ControllerAdministration from '../components/platform/Pts2ControllerAdministration.vue'
import {
  apiErrorMessage,
  createNaftaPosInstance,
  deleteNaftaPosInstance,
  deletePhysicalStation,
  getPhysicalStation,
  getPhysicalStations,
  testNaftaPosInstance,
  updateNaftaPosInstance,
  updatePhysicalStation,
  updatePhysicalStationStatus,
} from '../api.js'
import { useI18n } from '../i18n.js'
import { hasPermission } from '../session.js'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const station = ref(null)
const stationOptions = ref([])
const loading = ref(true)
const saving = ref(false)
const testingId = ref(null)
const error = ref('')
const notice = ref('')
const tab = ref('main')
const tabsNavigation = ref(null)
const sourceFormOpen = ref(false)
const editingSourceId = ref(null)
const pts2ControllerCount = ref(0)
const canUpdateStation = computed(() => hasPermission('stations.configuration.update'))
const canDeleteStation = computed(() => hasPermission('stations.configuration.delete'))
const canReadNaftaPos = computed(() => hasPermission('naftapos.instances.read'))
const canCreateNaftaPos = computed(() => hasPermission('naftapos.instances.create'))
const canUpdateNaftaPos = computed(() => hasPermission('naftapos.instances.update'))
const canDeleteNaftaPos = computed(() => hasPermission('naftapos.instances.delete'))
const canTestNaftaPos = computed(() => hasPermission('naftapos.instances.test'))
const canReadPts2 = computed(() => hasPermission('pts2.controllers.read') && station.value?.pts2AdministrationAvailable)
const stationForm = reactive({ stableCode: '', displayName: '', address: '', timezone: '', status: 'ACTIVE', note: '', version: 1 })
const sourceForm = reactive({ displayName: '', host: '', port: 3306, databaseName: '', username: '', password: '', enabled: true, targetStationId: null, version: 1 })
const tabs = computed(() => [
  { id: 'main', label: t('basic'), visible: true },
  { id: 'naftapos', label: t('naftaPosWorkplaces'), count: station.value?.naftaPosInstances?.length ?? 0, visible: canReadNaftaPos.value },
  { id: 'pts2', label: t('pts2Controllers'), count: pts2ControllerCount.value, visible: canReadPts2.value },
  { id: 'access', label: t('accessAndStatus'), visible: true },
].filter((item) => item.visible))

async function revealActiveTab() {
  await nextTick()
  const navigation = tabsNavigation.value
  const activeTab = navigation?.querySelector('button.active')
  if (!navigation || !activeTab) return

  const targetLeft = activeTab.offsetLeft - (navigation.clientWidth - activeTab.offsetWidth) / 2
  navigation.scrollTo({ left: Math.max(0, targetLeft), behavior: 'auto' })
}

function syncStationForm() {
  if (!station.value) return
  Object.assign(stationForm, {
    stableCode: station.value.stableCode,
    displayName: station.value.displayName,
    address: station.value.address ?? '',
    timezone: station.value.timezone,
    status: station.value.status,
    note: station.value.note ?? '',
    version: station.value.version,
  })
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [detail, registry] = await Promise.all([
      getPhysicalStation(route.params.stationId),
      getPhysicalStations(),
    ])
    station.value = detail
    stationOptions.value = registry.stations ?? []
    syncStationForm()
    const requestedTab = typeof route.query.tab === 'string' ? route.query.tab : 'main'
    tab.value = tabs.value.some((item) => item.id === requestedTab) ? requestedTab : 'main'
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  } finally {
    loading.value = false
    await revealActiveTab()
  }
}

async function saveStation() {
  saving.value = true
  error.value = ''
  try {
    station.value = await updatePhysicalStation(station.value.id, { ...stationForm })
    syncStationForm()
    notice.value = t('stationSaved')
  } catch (cause) {
    error.value = apiErrorMessage(t, cause, 'platformStationSaveFailed')
  } finally {
    saving.value = false
  }
}

function openSourceForm(source = null) {
  editingSourceId.value = source?.id ?? null
  Object.assign(sourceForm, source ? {
    displayName: source.displayName, host: source.host, port: source.port, databaseName: source.databaseName,
    username: source.username, password: '', enabled: source.enabled, targetStationId: source.stationId, version: source.version,
  } : {
    displayName: '', host: '', port: 3306, databaseName: '', username: '', password: '', enabled: true,
    targetStationId: station.value.id, version: 1,
  })
  sourceFormOpen.value = true
}

async function saveSource() {
  saving.value = true
  error.value = ''
  try {
    if (editingSourceId.value) {
      await updateNaftaPosInstance(station.value.id, editingSourceId.value, { ...sourceForm })
    } else {
      const { targetStationId, version, ...payload } = sourceForm
      await createNaftaPosInstance(station.value.id, payload)
    }
    sourceFormOpen.value = false
    notice.value = t('naftaPosWorkplaceSaved')
    await load()
    tab.value = 'naftapos'
  } catch (cause) {
    error.value = apiErrorMessage(t, cause, 'naftaPosWorkplaceSaveFailed')
  } finally {
    saving.value = false
  }
}

async function testSource(source) {
  testingId.value = source.id
  error.value = ''
  try {
    const result = await testNaftaPosInstance(station.value.id, source.id)
    notice.value = t('naftaPosConnectionOk', { elapsed: result.elapsedMs })
  } catch (cause) {
    error.value = apiErrorMessage(t, cause, 'naftaPosConnectionFailed')
  } finally {
    testingId.value = null
  }
}

async function removeSource(source) {
  if (!window.confirm(t('deleteNaftaPosWorkplaceConfirm', { name: source.displayName }))) return
  try {
    await deleteNaftaPosInstance(station.value.id, source.id, source.version)
    await load()
    tab.value = 'naftapos'
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  }
}

async function toggleStatus() {
  const nextStatus = station.value.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
  if (nextStatus === 'INACTIVE' && !window.confirm(t('disablePhysicalStationConfirm'))) return
  try {
    station.value = await updatePhysicalStationStatus(station.value.id, nextStatus, station.value.version)
    syncStationForm()
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  }
}

async function removeStation() {
  if (!window.confirm(t('deletePhysicalStationConfirm', { name: station.value.displayName }))) return
  try {
    await deletePhysicalStation(station.value.id, station.value.version)
    await router.push('/platform/stations')
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  }
}

onMounted(() => {
  window.addEventListener('resize', revealActiveTab)
  load()
})
onBeforeUnmount(() => window.removeEventListener('resize', revealActiveTab))
</script>

<template>
  <section class="platform-page station-detail-page">
    <RouterLink class="back-link" to="/platform/stations">← {{ t('physicalStations') }}</RouterLink>
    <p v-if="loading" class="platform-loading">{{ t('loading') }}</p>
    <template v-else-if="station">
      <header class="platform-page-heading station-detail-heading">
        <div><p class="eyebrow">{{ station.stableCode }}</p><h1>{{ station.displayName }}</h1><p>{{ station.address || t('addressNotSpecified') }} · {{ station.timezone }}</p></div>
        <b class="status-pill" :class="station.status.toLowerCase()">{{ station.status === 'ACTIVE' ? t('active') : t('disabled') }}</b>
      </header>
      <p v-if="error" class="notice error" role="alert">{{ error }}</p>
      <p v-if="notice" class="notice success" role="status">{{ notice }}</p>
      <nav ref="tabsNavigation" class="station-detail-tabs" :aria-label="t('stationSections')">
        <button v-for="item in tabs" :key="item.id" type="button" :class="{ active: tab === item.id }" @click="tab = item.id">
          {{ item.label }} <span v-if="item.count != null">{{ item.count }}</span>
        </button>
      </nav>

      <form v-if="tab === 'main'" class="platform-form-card" @submit.prevent="saveStation">
        <div class="platform-form-grid">
          <label>{{ t('stationStableCode') }}<input v-model="stationForm.stableCode" disabled /></label>
          <label>{{ t('stationName') }}<input v-model.trim="stationForm.displayName" :disabled="!canUpdateStation" required /></label>
          <label class="wide">{{ t('address') }}<input v-model.trim="stationForm.address" :disabled="!canUpdateStation" /></label>
          <label>{{ t('timezone') }}<input v-model.trim="stationForm.timezone" :disabled="!canUpdateStation" required /></label>
          <label class="wide">{{ t('note') }}<textarea v-model.trim="stationForm.note" rows="4" :disabled="!canUpdateStation"></textarea></label>
        </div>
        <footer v-if="canUpdateStation"><button class="button platform-primary" :disabled="saving" type="submit">{{ saving ? t('saving') : t('saveChanges') }}</button></footer>
      </form>

      <section v-else-if="tab === 'naftapos'" class="platform-panel">
        <header><div><h2>{{ t('naftaPosWorkplaces') }}</h2><p>{{ t('naftaPosWorkplacesHint') }}</p></div><button v-if="canCreateNaftaPos" class="button platform-primary" type="button" @click="openSourceForm()"><PlusIcon aria-hidden="true" /> {{ t('addWorkplace') }}</button></header>
        <form v-if="sourceFormOpen" class="source-editor" @submit.prevent="saveSource">
          <div class="platform-form-grid">
            <label>{{ t('workplaceName') }}<input v-model.trim="sourceForm.displayName" required /></label>
            <label>{{ t('host') }}<input v-model.trim="sourceForm.host" required /></label>
            <label>{{ t('port') }}<input v-model.number="sourceForm.port" type="number" min="1" max="65535" required /></label>
            <label>{{ t('databaseName') }}<input v-model.trim="sourceForm.databaseName" required /></label>
            <label>{{ t('databaseUser') }}<input v-model.trim="sourceForm.username" required /></label>
            <label>{{ t('databasePassword') }}<input v-model="sourceForm.password" type="password" :required="!editingSourceId" /><small v-if="editingSourceId">{{ t('leavePasswordEmpty') }}</small></label>
            <label v-if="editingSourceId" class="wide">{{ t('physicalStationForWorkplace') }}
              <select v-model.number="sourceForm.targetStationId" required>
                <option v-for="item in stationOptions" :key="item.id" :value="item.id">{{ item.displayName }} · {{ item.stableCode }}</option>
              </select>
              <small>{{ t('physicalStationForWorkplaceHint') }}</small>
            </label>
            <label class="checkbox wide"><input v-model="sourceForm.enabled" type="checkbox" /> {{ t('integrationEnabled') }}</label>
          </div>
          <footer><button class="button button-secondary" type="button" @click="sourceFormOpen = false">{{ t('cancel') }}</button><button class="button platform-primary" :disabled="saving" type="submit">{{ t('saveChanges') }}</button></footer>
        </form>
        <div v-if="station.naftaPosInstances.length" class="source-list">
          <article v-for="source in station.naftaPosInstances" :key="source.id">
            <span class="source-icon"><CircleStackIcon aria-hidden="true" /></span>
            <div><strong>{{ source.displayName }}</strong><small>{{ source.host }}:{{ source.port }}/{{ source.databaseName }}</small><em>{{ source.hasStoredPassword ? t('credentialsConfigured') : t('credentialsMissing') }}</em></div>
            <b class="status-pill" :class="source.enabled ? 'active' : 'inactive'">{{ source.enabled ? t('active') : t('disabled') }}</b>
            <div class="source-actions"><button v-if="canTestNaftaPos" class="button button-small button-secondary" type="button" :disabled="testingId === source.id" @click="testSource(source)">{{ testingId === source.id ? t('testing') : t('testConnection') }}</button><button v-if="canUpdateNaftaPos" class="button button-small button-secondary" type="button" @click="openSourceForm(source)">{{ t('edit') }}</button><button v-if="canDeleteNaftaPos" class="icon-button danger" type="button" :title="t('delete')" @click="removeSource(source)"><TrashIcon aria-hidden="true" /></button></div>
          </article>
        </div>
        <div v-else class="catalog-empty-state compact"><CircleStackIcon class="catalog-empty-icon" aria-hidden="true" /><strong>{{ t('noNaftaPosWorkplaces') }}</strong><p>{{ t('noNaftaPosWorkplacesHint') }}</p></div>
      </section>

      <section v-else-if="tab === 'pts2'" class="platform-panel">
        <Pts2ControllerAdministration
          :station="station"
          :station-options="stationOptions"
          :initial-controller-id="route.query.controllerId"
          @count="pts2ControllerCount = $event"
        />
      </section>

      <section v-else class="platform-panel station-access-panel">
        <header><div><h2>{{ t('accessAndStatus') }}</h2><p>{{ t('stationAccessHint') }}</p></div></header>
        <div class="station-status-row"><div><strong>{{ t('physicalStationStatus') }}</strong><p>{{ station.status === 'ACTIVE' ? t('activeStationAllowsIntegrations') : t('inactiveStationBlocksIntegrations') }}</p></div><button v-if="canUpdateStation" class="button" :class="station.status === 'ACTIVE' ? 'button-danger' : 'platform-primary'" type="button" @click="toggleStatus">{{ station.status === 'ACTIVE' ? t('disableStation') : t('enableStation') }}</button></div>
        <div v-if="canDeleteStation" class="danger-zone"><div><strong>{{ t('deletePhysicalStation') }}</strong><p>{{ t('deletePhysicalStationHint') }}</p></div><button class="button button-danger" type="button" @click="removeStation">{{ t('delete') }}</button></div>
      </section>
    </template>
    <p v-if="error && !station" class="notice error" role="alert">{{ error }}</p>
  </section>
</template>
